// Client-side helpers for the current user's public profile
// (public.users row — display name + avatar).

import { supabase } from './supabase';

export const AVATAR_COLORS = [
  { id: 'blue',   swatch: '#3b82f6' },
  { id: 'green',  swatch: '#10b981' },
  { id: 'pink',   swatch: '#ec4899' },
  { id: 'purple', swatch: '#8b5cf6' },
  { id: 'amber',  swatch: '#f59e0b' },
  { id: 'red',    swatch: '#ef4444' },
  { id: 'teal',   swatch: '#14b8a6' },
  { id: 'slate',  swatch: '#64748b' }
];

export const AVATAR_EMOJIS = [
  '😀', '😎', '🥳', '🤓', '🦊', '🐼', '🐧', '🦄',
  '🐯', '🌸', '🌈', '🔥', '⭐️', '🎉', '🍀', '🚀'
];

// Load the caller's profile row.
// Returns `{ displayName, avatarEmoji, avatarColor, avatarUrl, avatarKind }`
// or null if the user isn't signed in.
export async function loadProfile() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from('users')
    .select('display_name, avatar_emoji, avatar_color, avatar_url, avatar_kind')
    .eq('id', user.id)
    .maybeSingle();
  if (error && error.code !== 'PGRST116') throw error;
  return {
    displayName: data?.display_name || '',
    avatarEmoji: data?.avatar_emoji || '',
    avatarColor: data?.avatar_color || 'blue',
    avatarUrl: data?.avatar_url || '',
    avatarKind: data?.avatar_kind || 'emoji'
  };
}

// Upsert the caller's profile row. Any field left undefined is left unchanged.
export async function saveProfile({ displayName, avatarEmoji, avatarColor, avatarUrl, avatarKind }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const patch = { id: user.id };
  if (displayName !== undefined) patch.display_name = displayName || null;
  if (avatarEmoji !== undefined) patch.avatar_emoji = avatarEmoji || null;
  if (avatarColor !== undefined) patch.avatar_color = avatarColor || null;
  if (avatarUrl   !== undefined) patch.avatar_url   = avatarUrl   || null;
  if (avatarKind  !== undefined) patch.avatar_kind  = avatarKind  || 'emoji';
  const { error } = await supabase
    .from('users')
    .upsert(patch, { onConflict: 'id' });
  if (error) throw error;
}

// Upload a new profile photo to Supabase Storage (bucket `avatars`).
// Stored at `<uid>/avatar-<timestamp>.<ext>` so public URLs bust the CDN
// cache whenever the user uploads a new picture.
// Returns the public URL.
export async function uploadProfilePhoto(file) {
  if (!file) throw new Error('No file');
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');

  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `${user.id}/avatar-${Date.now()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from('avatars')
    .upload(path, file, {
      cacheControl: '3600',
      upsert: true,
      contentType: file.type || 'image/jpeg'
    });
  if (upErr) throw upErr;

  const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path);
  return pub.publicUrl;
}
